import React, { useState, useEffect } from "react";
import { Modal, Button, Card, Row, Col, Typography, Tag, Progress } from "antd";
import { 
  PlayCircleOutlined, 
  PauseCircleOutlined, 
  AudioOutlined, 
  FileTextOutlined,
  DownloadOutlined,
  CloseOutlined
} from "@ant-design/icons";
import moment from "moment";
import { capitalizeFirstLetter } from "../../../../utils/utils";
import ModelWrapper from "../../../modal/ModelWrapper";

const { Text, Title } = Typography;

const AudioPlayerModal = ({
  visible,
  onCancel,
  recording,
  selectedFileType,
  audioUrl,
  onDownload,
  setVisible
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioElement, setAudioElement] = useState(null);

  useEffect(() => {
    if (visible && audioUrl) {
      const audio = new Audio(audioUrl);
      setAudioElement(audio);
      
      audio.addEventListener('loadedmetadata', () => {
        setDuration(audio.duration);
      });
      
      audio.addEventListener('timeupdate', () => {
        setCurrentTime(audio.currentTime);
      });
      
      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        setCurrentTime(0);
      });
      
      audio.addEventListener('play', () => {
        setIsPlaying(true);
      });
      
      audio.addEventListener('pause', () => {
        setIsPlaying(false);
      });
      
      return () => {
        audio.pause();
        audio.removeEventListener('loadedmetadata', () => {});
        audio.removeEventListener('timeupdate', () => {});
        audio.removeEventListener('ended', () => {});
        audio.removeEventListener('play', () => {});
        audio.removeEventListener('pause', () => {});
      };
    }
  }, [visible, audioUrl]);

  const togglePlayPause = () => {
    if (audioElement) {
      if (isPlaying) {
        audioElement.pause();
      } else {
        audioElement.play();
      }
    }
  };

  const handleSeek = (e) => {
    if (audioElement) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const seekTime = (clickX / width) * duration;
      audioElement.currentTime = seekTime;
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getFileInfo = () => {
    if (selectedFileType === "original") {
      return {
        title: "Original Recording",
        name: recording.name,
        createdAt: recording.createdAt,
        status: recording.status,
        uploadedBy: recording.user?.name || "Unknown",
        icon: <AudioOutlined style={{ fontSize: "32px", color: "#8A3EF4" }} />,
        tagColor: "blue",
        tagText: "Original"
      };
    } else if (selectedFileType === "cloned") {
      return {
        title: "Cloned Recording",
        name: recording.clonedFiles.name,
        createdAt: recording.clonedFiles.uploadedBy ? recording.createdAt : "Recently uploaded",
        status: "cloned",
        uploadedBy: "Cloned version",
        comment: recording.clonedFiles.comment,
        icon: <FileTextOutlined style={{ fontSize: "32px", color: "#52c41a" }} />,
        tagColor: "green",
        tagText: "Cloned"
      };
    }
  };

  const fileInfo = getFileInfo();

  return (
    <ModelWrapper
      title={
        <div className="d-flex align-items-center justify-start gap-12">
          <AudioOutlined style={{ fontSize: "20px", color: "#8A3EF4" }} />
          <span className="font-18 font-semibold text-primary">
            Audio Player
          </span>
        </div>
      }
      show={visible}
      setShow={setVisible}
      onCancel={onCancel}
      footer={null}
      width={600}
      centered
      destroyOnClose
      maskClosable={false}
    >
      <Card
        style={{
          borderRadius: "12px",
          border: "1px solid var(--border-color)",
          backgroundColor: "var(--secondary-bg)"
        }}
        bodyStyle={{ padding: "24px" }}
      >
        {/* File Information */}
        <div className="d-flex align-items-center justify-start gap-16 mb-24">
          {fileInfo.icon}
          <div style={{ flex: 1 }}>
            <div className="d-flex align-items-center justify-start gap-8 mb-8">
              <Title level={5} style={{ margin: 0, color: "var(--primary-text-color)" }}>
                {fileInfo.title}
              </Title>
              <Tag color={fileInfo.tagColor} style={{ fontSize: "12px", marginLeft: "10px" }}>
                {fileInfo.tagText}
              </Tag>
            </div>
            <Text className="font-16 font-semibold text-primary">
              {capitalizeFirstLetter(fileInfo.name)}
            </Text>
          </div>
        </div>

        {/* File Details */}
        <Row gutter={[16, 12]} className="mb-24">
          <Col span={12}>
            <Text className="font-12 text-secondry">Recorded by:</Text>
            <div>
              <Text className="font-14 text-primary">{fileInfo.uploadedBy}</Text>
            </div>
          </Col>
          <Col span={12}>
            <Text className="font-12 text-secondry">Created:</Text>
            <div>
              <Text className="font-14 text-primary">
                {moment(fileInfo.createdAt).format("DD MMM YYYY hh:mm A")}
              </Text>
            </div>
          </Col>
          {fileInfo.comment && (
            <Col span={24}>
              <Text className="font-12 text-secondry">Comment:</Text>
              <div>
                <Text className="font-14 text-primary">{fileInfo.comment}</Text>
              </div>
            </Col>
          )}
        </Row>

        {/* Audio Player */}
        {/* <div className="mb-24">
          <div className="d-flex justify-content-center mb-16">
            <Button
              type="text"
              size="large"
              icon={
                isPlaying ? (
                  <PauseCircleOutlined style={{ fontSize: "48px", color: "#8A3EF4" }} />
                ) : (
                  <PlayCircleOutlined style={{ fontSize: "48px", color: "#8A3EF4" }} />
                )
              }
              onClick={togglePlayPause}
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "var(--primary-bg)",
                border: "2px solid #8A3EF4"
              }}
            />
          </div>

 
          <div className="mb-8">
            <div
              style={{
                width: "100%",
                height: "6px",
                backgroundColor: "var(--border-color)",
                borderRadius: "3px",
                cursor: "pointer",
                position: "relative"
              }}
              onClick={handleSeek}
            >
              <div
                style={{
                  width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
                  height: "100%",
                  backgroundColor: "#8A3EF4",
                  borderRadius: "3px",
                  transition: "width 0.1s ease"
                }}
              />
            </div>
          </div>


          <div className="d-flex justify-content-between">
            <Text className="font-12 text-secondry">
              {moment.utc(currentTime * 1000).format('mm:ss')}
            </Text>
            <Text className="font-12 text-secondry">
              {moment.utc(duration * 1000).format('mm:ss')}
            </Text>
          </div>
        </div> */}

{audioUrl && (
            <audio
              controls
              autoPlay
              src={audioUrl}
              style={{ width: "100%" }}
            />
          )}

        {/* Action Buttons */}
        <div className="d-flex justify-content-between align-items-center mt-24" style={{gap: "10px"}}>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => {
              if (selectedFileType === "original") {
                onDownload(recording.recording);
              } else if (selectedFileType === "cloned") {
                onDownload(recording.clonedFiles.fileUrl);
              }
            }}
            style={{
              backgroundColor: "#8A3EF4",
              border: "1px solid var(--border-color)",
              borderRadius: "8px",
              height: "40px",
              padding: "0 24px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "var(--primary-text-color)"
            }}
          >
            Download Recording
          </Button>
          
          <Button
            type="default"
            icon={<CloseOutlined />}
            onClick={onCancel}
            style={{
              border: "1px solid var(--border-color)",
              borderRadius: "8px",
              height: "40px",
              padding: "0 24px",
              display: "flex", 
              alignItems: "center",
              gap: "8px",
              color: "var(--primary-text-color)"
            }}
          >
            Close Player
          </Button>
        </div>
      </Card>
    </ModelWrapper>
  );
};

export default AudioPlayerModal; 